/**
 * @module app.shared
 */

import { autoinject, bindable, bindingMode } from 'aurelia-framework';
import { copyElementContentsToClipboard } from 'app/shared/lib/clipboard';

const allInfoTips = new Map<string, EssInfoTip>();

type InfoTipType = 'debug' | 'info' | 'warn' | 'error';

/**
 * Show an info tip
 * @param uniqueId which `<ess-info-tip>` element to target, use `'global'` for the app-wide tip
 * @param title title text for the tip
 * @param text body text of the tip
 * @param type tip level (`debug` | `info` | `warn` | `error`)
 */
export function showInfoTip(uniqueId: string, title: string, text: string, type: InfoTipType = 'info'): void {
    const tip = allInfoTips.get(uniqueId);
    if(tip) {
        const visible = !!(title || text);
        tip.visible = visible;
        tip.title = title || '';
        tip.text = text || '';
        tip.type = type || 'info';
        if(visible) {
            console[type](`${title || 'Info Tip'}: ${text}`);
        }
    }
}

/**
 * Hide an info tip
 * @param uniqueId which `<ess-info-tip>` element to target, use `'global'` for the app-wide tip
 */
export function hideInfoTip(uniqueId: string): void {
    showInfoTip(uniqueId, '', '', 'info');
}

@autoinject
export class EssInfoTip {

    // one-time binding because this cannot change while the info-tip is attached
    @bindable({ defaultBindingMode: bindingMode.oneTime })
    public uniqueId: string = '';

    // used in the HTML
    public visible: boolean = false;
    public title: string = '';
    public text: string = '';
    public type: string = '';

    constructor() { }

    /**
     * Lifecycle: Attached
     */
    protected attached(): void {
        const id = this.uniqueId;
        if(id) {
            // add to map
            allInfoTips.set(id, this);
        }
    }

    /**
     * Lifecycle: Detached
     */
    protected detached(): void {
        const id = this.uniqueId;
        if(id) {
            allInfoTips.delete(id);
        }
    }

    // called from "Copy" button in HTML
    protected copy(element: Element): void {
        copyElementContentsToClipboard(element);
    }

    // called from "Close" button in HTML
    protected close(): void {
        this.visible = false;
        this.text = '';
        this.title = '';
        this.type = 'info';
    }

}
